import * as React from 'react';
import { Plus } from 'lucide-react';
import { Button, cn } from '@repo/ui-web';
import { buttonSizes, buttonStates, buttonVariants } from '../../catalog/component-states';
import { StateToggle } from '../../components/StateToggle';

type ButtonState = (typeof buttonStates)[number];
type ButtonSize = (typeof buttonSizes)[number];

type ButtonStateConfig = {
  label: string;
  disabled?: boolean;
  loading?: boolean;
};

const buttonStateConfig: Record<ButtonState, ButtonStateConfig> = {
  default: { label: 'Primary action' },
  hover: { label: 'Primary action' },
  disabled: { label: 'Primary action', disabled: true },
  loading: { label: 'Primary action', disabled: true, loading: true },
  overflow: { label: 'This is a very long button label that overflows' },
};

const sizeStableW: Partial<Record<ButtonSize, { min: string; max: string }>> = {
  lg: { min: 'min-w-44', max: 'max-w-44' },
  md: { min: 'min-w-40', max: 'max-w-40' },
  sm: { min: 'min-w-32', max: 'max-w-32' },
  xs: { min: 'min-w-28', max: 'max-w-28' },
};

export function ButtonShowcase() {
  const [state, setState] = React.useState<ButtonState>('default');
  const [variant, setVariant] = React.useState<(typeof buttonVariants)[number]>('default');
  const [size, setSize] = React.useState<ButtonSize>('lg');
  const config = buttonStateConfig[state];
  const stableW = sizeStableW[size];

  return (
    <div className="space-y-4">
      <StateToggle options={buttonStates} value={state} onChange={setState} label="State" />
      <StateToggle
        options={buttonVariants}
        value={variant}
        onChange={setVariant}
        label="Variant"
        formatLabel={(v) => {
          const labels: Record<string, string> = {
            default: 'primary',
            outline: 'primary outline',
            'secondary-outline': 'secondary outline',
            'destructive-outline': 'destructive outline',
          };
          return labels[v] ?? v;
        }}
      />
      <StateToggle options={buttonSizes} value={size} onChange={setSize} label="Size" />
      <div className="flex min-h-[72px] items-center justify-center rounded-xl border border-dashed border-border p-6">
        <Button
          variant={variant}
          size={size}
          disabled={config.disabled}
          loading={config.loading}
          className={cn(
            state === 'hover' && variant === 'default' && 'bg-primary/90',
            state === 'hover' && variant === 'secondary' && 'bg-secondary/80',
            state === 'hover' && variant === 'destructive' && 'bg-destructive/90',
            state === 'hover' && variant === 'outline' && 'bg-primary/10',
            state === 'hover' && variant === 'secondary-outline' && 'bg-secondary/20',
            state === 'hover' && variant === 'destructive-outline' && 'bg-destructive/10',
            stableW?.min,
            state === 'overflow' && stableW?.max,
          )}
          aria-label={size === 'icon' || size === 'icon-xs' ? config.label : undefined}
        >
          {size === 'icon' || size === 'icon-xs' ? (
            <Plus className={size === 'icon-xs' ? 'h-3 w-3' : 'h-4 w-4'} />
          ) : (
            <span className={cn(state === 'overflow' && 'truncate')}>{config.label}</span>
          )}
        </Button>
      </div>
    </div>
  );
}
